import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { LoadMoreSentinel } from '../../shared/load_more_sentinel.js';
import { scrollParentOf } from '../../shared/results_page/scroll_parent.js';
import {
  ResultsNotice,
  type ResultsDecisionNotice,
} from '../../shared/results_page/results_notice.js';
import type { BusOfferView } from '../models.js';
import { BusOfferCard, type BusOfferCardCopy } from './bus_offer_card.js';
import './bus_results_list.css';

/** Quantas viagens a lista mostra de cada vez — a rolagem até o fim abre a
 *  próxima leva. A lista inteira já está no cliente. */
const BUS_RESULTS_PAGE_SIZE = 10;

const PENDING_ROWS = 3;

/** As linhas fantasma da espera, na caixa do card real. */
function BusPendingRows(): ReactNode {
  return (
    <>
      {Array.from({ length: PENDING_ROWS }, (_, slot) => (
        <li key={slot} aria-hidden="true" className="hui-bus-results-pending">
          <div className="hui-bus-offer-card">
            <div className="hui-bus-offer-main">
              <div className="hui-bus-offer-trip">
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-32" />
              </div>
              <div className="hui-bus-offer-side">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-20" />
              </div>
            </div>
          </div>
        </li>
      ))}
    </>
  );
}

export type BusResultsListProps = {
  offers: readonly BusOfferView[];
  /** O nome acessível da lista ("Ônibus encontrados"). */
  resultsLabel: string;
  cardCopy: BusOfferCardCopy;
  /** "Ver mais ônibus" — o nome do botão da sentinela (o caminho de teclado). */
  loadMoreLabel: string;
  selectedOfferId?: string;
  /** O card aberto na escolha de poltrona, e o que abre nele. */
  expandedOfferId?: string;
  renderExpansion?: (offerId: string) => ReactNode;
  busy?: boolean;
  notice?: ResultsDecisionNotice;
  pending?: boolean;
  /** Muda quando o corte ou a ordem mudam: a leva volta à primeira. */
  resetKey: string;
  /** O corte acabou de mudar (#6421): a lista se mostra em atualização por um
   *  instante, e quem rolou além do topo dela volta ao início. */
  refreshing?: boolean;
  /** O que a lista mostra quando o corte esconde tudo. */
  empty?: ReactNode;
  onDetailsOpen?: ((offerId: string) => void) | undefined;
  onSelect(offerId: string): void;
};

/**
 * O corpo da lista de ônibus: o aviso de estado, os cards e a rolagem
 * infinita — a `LoadMoreSentinel` do hotel e do carro. A lista do dia chega
 * INTEIRA (o corte e a ordem rodam no cliente), então a sentinela só revela a
 * próxima leva. Um corte ou ordem novos (`resetKey`) voltam a leva à primeira
 * SEM remontar os cards: cada passo do slider de preço muda o corte, e remontar
 * a lista por passo travava a alça (#6416).
 */
export function BusResultsList({
  offers,
  resultsLabel,
  cardCopy,
  loadMoreLabel,
  selectedOfferId,
  expandedOfferId,
  renderExpansion,
  busy = false,
  notice,
  pending = false,
  resetKey,
  refreshing = false,
  empty,
  onDetailsOpen,
  onSelect,
}: Readonly<BusResultsListProps>): ReactNode {
  const [shown, setShown] = useState(BUS_RESULTS_PAGE_SIZE);
  const [shownFor, setShownFor] = useState(resetKey);
  if (shownFor !== resetKey) {
    setShownFor(resetKey);
    setShown(BUS_RESULTS_PAGE_SIZE);
  }
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const list = listRef.current;
    if (!refreshing || !list) return;
    // Só quem rolou além do topo da lista: com o topo à vista, a página fica.
    const top = scrollParentOf(list)?.getBoundingClientRect().top ?? 0;
    if (list.getBoundingClientRect().top < top) list.scrollIntoView({ block: 'start' });
  }, [refreshing]);
  const refreshingAttr = refreshing || undefined;
  if (offers.length === 0 && !pending && empty) {
    // O aviso da decisão (ex.: a ida que sumiu) sobrevive ao estado vazio.
    return (
      <>
        {notice && <ResultsNotice notice={notice} />}
        <div className="hui-bus-results-empty" aria-busy={refreshingAttr}>
          {empty}
        </div>
      </>
    );
  }
  return (
    <>
      {notice && <ResultsNotice notice={notice} />}
      <ul
        ref={listRef}
        className="hui-bus-results-list"
        aria-label={resultsLabel}
        aria-busy={refreshingAttr}
      >
        {offers.slice(0, shown).map((offer) => (
          <li key={offer.id}>
            <BusOfferCard
              offer={offer}
              copy={cardCopy}
              selected={selectedOfferId === offer.id}
              busy={busy}
              {...(expandedOfferId === offer.id && renderExpansion
                ? { expansion: renderExpansion(offer.id) }
                : {})}
              {...(onDetailsOpen ? { onDetailsOpen } : {})}
              onSelect={onSelect}
            />
          </li>
        ))}
        {pending && <BusPendingRows />}
      </ul>
      <LoadMoreSentinel
        hasMore={offers.length > shown}
        loading={false}
        label={loadMoreLabel}
        onLoadMore={() => {
          setShown((current) => current + BUS_RESULTS_PAGE_SIZE);
        }}
      />
    </>
  );
}
